import { Box, Flex, Tooltip } from '@chakra-ui/react';
import { PercentageNumberInput } from 'components/NumberInput';
import Decimal from 'decimal.js';
import {
  IRR_CAP_PERIOD,
  IRR_CAP_PERIOD_OPTIONS,
  convertRateForPeriodChange,
  hasExtraFractionalDigits,
  isIncompleteRateInput,
} from 'utils/irrCapPeriod';
import { forwardRef, useState } from 'react';
import SelectInput from './SelectInput';

const PERIOD_SELECT_WIDTH = '110px';
const PERCENT_INPUT_WIDTH = '140px';

// Persists the typed decimal rate and the selected period (monthly | annual).
// The server converts monthly to annual only when running XIRR / daily math.
const IrrCapPeriodInput = forwardRef(
  ({ value, period, onChange, onPeriodChange, isDisabled }, ref) => {
    const selectedPeriod = period || IRR_CAP_PERIOD.ANNUAL;
    const [draftDisplayRate, setDraftDisplayRate] = useState(null);

    const displayValue =
      draftDisplayRate !== null ? draftDisplayRate : value;

    const showFullPrecisionTooltip = hasExtraFractionalDigits(value);
    const fullPrecisionLabel =
      value != null && value !== ''
        ? `${new Decimal(value).times(100).toString()}% (${selectedPeriod})`
        : '';

    const handlePeriodChange = (nextPeriod) => {
      if (nextPeriod === selectedPeriod) {
        return;
      }

      setDraftDisplayRate(null);

      const converted = convertRateForPeriodChange(
        value,
        selectedPeriod,
        nextPeriod
      );

      onPeriodChange && onPeriodChange(nextPeriod);
      if (converted != null && converted !== undefined && !isNaN(converted)) {
        onChange && onChange(converted);
      }
    };

    const handleDisplayChange = (newDisplayValue) => {
      if (isIncompleteRateInput(newDisplayValue)) {
        setDraftDisplayRate(newDisplayValue);
        return;
      }

      setDraftDisplayRate(
        selectedPeriod === IRR_CAP_PERIOD.MONTHLY ? newDisplayValue : null
      );
      onChange && onChange(newDisplayValue);
    };

    const handleBlur = () => {
      setDraftDisplayRate(null);
    };

    return (
      <Flex ref={ref} gap={2} align="center" minW="260px">
        <Box w={PERIOD_SELECT_WIDTH} flex="0 0 auto">
          <SelectInput
            value={selectedPeriod}
            isDisabled={isDisabled}
            options={IRR_CAP_PERIOD_OPTIONS}
            onChange={handlePeriodChange}
            fontWeight={800}
          />
        </Box>
        <Box w={PERCENT_INPUT_WIDTH} flex="0 0 auto">
          <Tooltip
            label={fullPrecisionLabel}
            fontSize="xs"
            isDisabled={!showFullPrecisionTooltip}
          >
            <Box>
              <PercentageNumberInput
                key={selectedPeriod}
                value={displayValue}
                isDisabled={isDisabled}
                showPrefix
                w={PERCENT_INPUT_WIDTH}
                inputProps={{ fontWeight: 800 }}
                onChange={handleDisplayChange}
                onBlur={handleBlur}
              />
            </Box>
          </Tooltip>
        </Box>
      </Flex>
    );
  }
);

export default IrrCapPeriodInput;
